import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Plus, X, Search, Calendar, Banknote, Filter, CheckCircle2, AlertCircle,
  ArrowUpCircle, ArrowDownCircle, Download, Sparkles, ChevronDown, ChevronRight,
  Trash2, Loader2, RotateCcw, Pencil, FileSpreadsheet,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney, formatDate } from '@/lib/api'
import { useFilial } from '@/lib/filial'
import { enterAbrePrimeiro } from '@/lib/keyboard'
import { exportarExcel, numBR } from '@/lib/excel'
import { pedirExclusao } from '@/lib/confirmarExclusao'

interface Conta { id: number; nome: string; saldo_atual?: number }
interface Lancamento {
  id: number; conta_id: number; tipo: string; descricao: string
  valor: number; data: string; conciliado: boolean
  conciliado_em?: string | null
  conciliado_por_nome?: string | null
  origem: string; observacao?: string
  conta_gerencial_id?: number
  cg_codigo?: string; cg_nome?: string
  pessoa_nome?: string
  conta_financeira_id?: number
}

export default function LancamentosPro() {
  const qc = useQueryClient()
  // Loja selecionada no topo: entra na chave do cache pra a lista ser refeita
  // ao trocar de loja (senão o React Query devolve o resultado da loja anterior).
  const filialAtualId = useFilial(s => s.filialAtual?.id ?? null)
  const [showNovo, setShowNovo] = useState(false)
  const [editar, setEditar] = useState<Lancamento | null>(null)
  const [contaId, setContaId] = useState<number | ''>('')
  const [filtroTipo, setFiltroTipo] = useState<'todos' | 'receita' | 'despesa'>('todos')
  const [filtroConc, setFiltroConc] = useState<'todos' | 'sim' | 'nao'>('todos')
  const [filtroOrigem, setFiltroOrigem] = useState('')
  const [busca, setBusca] = useState('')
  const [dataIni, setDataIni] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [expandido, setExpandido] = useState<Set<number>>(new Set())

  const { data: contas = [] } = useQuery<Conta[]>({
    queryKey: ['contas-bancarias'],
    queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data,
  })

  // Contas gerenciais analíticas (pra classificar o lançamento na DRE)
  const { data: cgs = [] } = useQuery<any[]>({
    queryKey: ['cg-analiticas-lanc'],
    queryFn: async () => (await api.get('/plano-contas/analiticas')).data,
  })

  // Auto-seleciona MP como padrão se ainda não escolhido
  if (contas.length > 0 && contaId === '') {
    const mp = contas.find((c: any) => /mercado.*pago/i.test(c.nome || ''))
    if (mp) {
      setTimeout(() => setContaId(mp.id), 0)
    }
  }

  // filialAtualId entra na chave: trocar de loja no topo tem que refazer a busca,
  // senão o React Query devolve o resultado em cache da loja anterior.
  const { data: lancamentos = [], isLoading } = useQuery<Lancamento[]>({
    queryKey: ['lancamentos-pro', filialAtualId, contaId, filtroTipo, filtroConc, filtroOrigem, busca, dataIni, dataFim],
    queryFn: async () => {
      // Usa endpoint conciliacao/lancamentos quando há conta selecionada (mais detalhado)
      if (contaId) {
        const params: any = { conta_id: contaId, limit: 1000 }
        if (filtroConc === 'sim') params.conciliado = true
        if (filtroConc === 'nao') params.conciliado = false
        if (busca) params.busca = busca
        if (dataIni) params.data_ini = dataIni
        if (dataFim) params.data_fim = dataFim
        const data = (await api.get('/financeiro/conciliacao-v2/lancamentos', { params })).data
        // filtra tipo localmente
        return filtroTipo === 'todos' ? data : data.filter((l: any) => l.tipo === filtroTipo)
      }
      // Sem conta selecionada: usa endpoint legado
      const data = (await api.get('/financeiro/lancamentos')).data
      return data.filter((l: any) => {
        if (filtroTipo !== 'todos' && l.tipo !== filtroTipo) return false
        if (filtroConc === 'sim' && !l.conciliado) return false
        if (filtroConc === 'nao' && l.conciliado) return false
        if (filtroOrigem && l.origem !== filtroOrigem) return false
        if (busca) {
          const q = busca.toLowerCase()
          if (!(l.descricao || '').toLowerCase().includes(q)
            && !(l.observacao || '').toLowerCase().includes(q)) return false
        }
        if (dataIni && l.data < dataIni) return false
        if (dataFim && l.data > dataFim) return false
        return true
      })
    },
  })

  const excluir = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/financeiro/lancamentos/${id}`)).data,
    onSuccess: (d: any) => {
      toast.success('Lançamento excluído — saldo ajustado')
      qc.invalidateQueries({ queryKey: ['lancamentos-pro'] })
      qc.invalidateQueries({ queryKey: ['contas-bancarias'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao excluir'),
  })

  const salvarEdicao = useMutation({
    mutationFn: async (payload: any) => (await api.patch(`/financeiro/lancamentos/${payload.id}`, payload.body)).data,
    onSuccess: () => {
      toast.success('Lançamento atualizado')
      setEditar(null)
      qc.invalidateQueries({ queryKey: ['lancamentos-pro'] })
      qc.invalidateQueries({ queryKey: ['contas-bancarias'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  // Estorna a BAIXA: reverte este lançamento + saldo do banco e volta a conta a pagar/receber
  // pra ABERTA (pra corrigir o título e baixar de novo).
  const estornarBaixa = useMutation({
    mutationFn: async (contaFinId: number) => (await api.post(`/financeiro/${contaFinId}/estornar`)).data,
    onSuccess: () => {
      toast.success('Baixa estornada — conta voltou pra aberta (lançamento e saldo revertidos)')
      qc.invalidateQueries({ queryKey: ['lancamentos-pro'] })
      qc.invalidateQueries({ queryKey: ['contas-bancarias'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao estornar'),
  })

  const totais = useMemo(() => {
    let receita = 0, despesa = 0, conc = 0, naoconc = 0
    for (const l of lancamentos) {
      if (l.tipo === 'receita') receita += Number(l.valor || 0)
      else despesa += Number(l.valor || 0)
      if (l.conciliado) conc++
      else naoconc++
    }
    return { receita, despesa, saldo: receita - despesa, conc, naoconc }
  }, [lancamentos])

  // origens distintas pra filtro
  const origens = useMemo(() => {
    const s = new Set<string>()
    for (const l of lancamentos) s.add(l.origem || 'manual')
    return Array.from(s).sort()
  }, [lancamentos])

  const contaSel = contas.find(c => c.id === contaId)

  function toggleExp(id: number) {
    const s = new Set(expandido)
    s.has(id) ? s.delete(id) : s.add(id)
    setExpandido(s)
  }

  return (
    <div className="p-4 lg:p-6 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Banknote size={22} className="text-violet-600" /> Lançamentos
          </h1>
          <p className="text-xs text-ink-500">
            Movimentações reais nas contas bancárias. Inclui receitas, despesas, taxas e baixas automáticas.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportarExcel(`lancamentos-${new Date().toISOString().slice(0,10)}`, [
            { header: 'Data', key: 'data', fmt: (v: any) => formatDate(v) },
            { header: 'Tipo', key: 'tipo' },
            { header: 'Descrição', key: 'descricao' },
            { header: 'Conta gerencial', key: 'cg_nome', fmt: (_: any, r: any) => [r.cg_codigo, r.cg_nome].filter(Boolean).join(' ') },
            { header: 'Pessoa', key: 'pessoa_nome' },
            { header: 'Valor', key: 'valor', fmt: (v: any) => numBR(v) },
            { header: 'Origem', key: 'origem' },
            { header: 'Conciliado', key: 'conciliado', fmt: (v: any) => v ? 'Sim' : 'Não' },
            { header: 'Observação', key: 'observacao' },
          ], lancamentos)} disabled={!lancamentos.length}
            className="btn-secondary text-xs flex items-center gap-1.5 disabled:opacity-50">
            <FileSpreadsheet size={12} /> Gerar Excel
          </button>
          <Link to="/financeiro/conciliacao"
            className="btn-secondary text-xs flex items-center gap-1.5">
            <CheckCircle2 size={12} /> Conciliação
          </Link>
          <button onClick={() => setShowNovo(true)} className="btn-primary text-xs flex items-center gap-1.5">
            <Plus size={12} /> Novo
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white border border-ink-200 rounded-lg p-3 space-y-2">
        <div className="text-[10px] uppercase font-semibold text-ink-500 mb-1">Conta bancária</div>
        <div className="flex flex-wrap gap-1">
          <button onClick={() => setContaId('')}
            className={`text-xs px-2.5 py-1 rounded ${
              !contaId ? 'bg-violet-600 text-white' : 'bg-ink-100 hover:bg-ink-200'
            }`}>Todas</button>
          {contas.map(c => (
            <button key={c.id} onClick={() => setContaId(c.id)}
              className={`text-xs px-2.5 py-1 rounded ${
                contaId === c.id ? 'bg-violet-600 text-white' : 'bg-ink-100 hover:bg-ink-200'
              }`}>
              {c.nome}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center pt-2 border-t border-ink-100">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-ink-400" />
            <input className="input pl-7 text-xs h-8" placeholder="Buscar descrição, cliente..."
              value={busca} onChange={e => setBusca(e.target.value)}
              onKeyDown={enterAbrePrimeiro(lancamentos, (l) => setEditar(l))} />
          </div>
          <div className="flex items-center gap-1 text-xs">
            <Calendar size={12} className="text-ink-400" />
            <input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)}
              className="input text-xs h-8 w-36" />
            <span>-</span>
            <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)}
              className="input text-xs h-8 w-36" />
          </div>
          <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value as any)}
            className="input text-xs h-8 w-32">
            <option value="todos">Todos os tipos</option>
            <option value="receita">Receita</option>
            <option value="despesa">Despesa</option>
          </select>
          <select value={filtroConc} onChange={e => setFiltroConc(e.target.value as any)}
            className="input text-xs h-8 w-44">
            <option value="todos">Conciliação: todos</option>
            <option value="sim">Só conciliados</option>
            <option value="nao">Só pendentes</option>
          </select>
          {origens.length > 0 && (
            <select value={filtroOrigem} onChange={e => setFiltroOrigem(e.target.value)}
              className="input text-xs h-8 w-44">
              <option value="">Origem: todas</option>
              {origens.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          )}
          {(busca || dataIni || dataFim || filtroTipo !== 'todos' || filtroConc !== 'todos' || filtroOrigem) && (
            <button onClick={() => {
              setBusca(''); setDataIni(''); setDataFim('')
              setFiltroTipo('todos'); setFiltroConc('todos'); setFiltroOrigem('')
            }} className="text-xs px-2 py-1 rounded text-ink-500 hover:bg-ink-100">Limpar</button>
          )}
        </div>
      </div>

      {/* Totais do filtro */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <CardTot label="Receitas" valor={totais.receita} cor="emerald" />
        <CardTot label="Despesas" valor={totais.despesa} cor="rose" />
        <CardTot label="Saldo do filtro" valor={totais.saldo} cor={totais.saldo >= 0 ? 'emerald' : 'red'} />
        <CardTot label="Conciliados" valor={totais.conc} fmt="num" cor="blue" />
        <CardTot label="Pendentes" valor={totais.naoconc} fmt="num" cor="amber" />
      </div>

      {/* Tabela */}
      <div className="bg-white border border-ink-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-ink-50 sticky top-0 z-10">
            <tr className="text-[10px] uppercase font-bold text-ink-600">
              <th className="w-6"></th>
              <th className="text-left p-2">Data</th>
              <th className="text-left p-2">Tipo</th>
              <th className="text-left p-2">Descrição</th>
              <th className="text-left p-2">Conta gerencial</th>
              <th className="text-left p-2">Pessoa</th>
              <th className="text-right p-2">Valor</th>
              <th className="text-left p-2">Origem</th>
              <th className="text-center p-2">Conciliado</th>
              <th className="text-center p-2">Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={10} className="p-8 text-center text-ink-400">Carregando...</td></tr>
            ) : lancamentos.length === 0 ? (
              <tr><td colSpan={10} className="p-8 text-center text-ink-400">Sem lançamentos</td></tr>
            ) : lancamentos.map(l => (
              <>
                <tr key={l.id} className="border-t border-ink-100 hover:bg-blue-50/30">
                  <td className="text-center p-1">
                    {(l.observacao || l.cg_nome) && (
                      <button onClick={() => toggleExp(l.id)}
                        className="p-1 rounded hover:bg-ink-100 text-ink-400">
                        {expandido.has(l.id) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>
                    )}
                  </td>
                  <td className="p-2 font-mono text-[10px]">{formatDate(l.data)}</td>
                  <td className="p-2">
                    {l.tipo === 'receita'
                      ? <ArrowUpCircle size={12} className="text-emerald-600 inline" />
                      : <ArrowDownCircle size={12} className="text-rose-600 inline" />}
                  </td>
                  <td className="p-2 max-w-md truncate">{l.descricao}</td>
                  <td className="p-2 text-[10px] text-ink-500 truncate max-w-[150px]">
                    {l.cg_codigo && <><span className="font-mono">{l.cg_codigo}</span> {l.cg_nome}</>}
                  </td>
                  <td className="p-2 text-[10px] truncate max-w-[120px]">{l.pessoa_nome || '—'}</td>
                  <td className={`text-right p-2 font-mono font-semibold ${l.tipo === 'receita' ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {l.tipo === 'receita' ? '+' : '−'}{formatMoney(Number(l.valor))}
                  </td>
                  <td className="p-2">
                    <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded font-semibold ${
                      l.origem === 'auto_mp' ? 'bg-violet-100 text-violet-800'
                        : l.origem === 'baixa' || l.origem === 'baixa_marketplace' ? 'bg-blue-100 text-blue-800'
                        : 'bg-ink-100 text-ink-700'
                    }`}>{l.origem}</span>
                  </td>
                  <td className="text-center p-2">
                    {l.conciliado
                      ? <span title={`Por ${l.conciliado_por_nome || '—'}`}>
                          <CheckCircle2 size={13} className="text-emerald-600 inline" />
                        </span>
                      : <span className="text-[10px] text-amber-700">pendente</span>}
                  </td>
                  <td className="text-center p-2 whitespace-nowrap">
                    <button
                      onClick={() => setEditar(l)}
                      className="p-1 rounded hover:bg-blue-50 text-blue-600"
                      title="Editar lançamento">
                      <Pencil size={13} />
                    </button>
                    {l.origem === 'manual' ? (
                      <button
                        onClick={() => pedirExclusao({ descricao: `o lançamento avulso "${l.descricao}" — o saldo do banco será ajustado de volta`, onConfirm: () => excluir.mutate(l.id) })}
                        disabled={excluir.isPending}
                        className="p-1 rounded hover:bg-rose-50 text-rose-600 disabled:opacity-40"
                        title="Excluir lançamento avulso">
                        {excluir.isPending && excluir.variables === l.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                      </button>
                    ) : (l.origem === 'baixa' || l.origem === 'baixa_marketplace') && l.conta_financeira_id ? (
                      <button
                        onClick={() => { if (confirm('Estornar esta baixa?\n\nReverte o lançamento + o saldo do banco e volta a conta pra ABERTA, pra você corrigir o título e baixar de novo.')) estornarBaixa.mutate(l.conta_financeira_id!) }}
                        disabled={estornarBaixa.isPending}
                        className="p-1 rounded hover:bg-amber-50 text-amber-700 disabled:opacity-40"
                        title="Estornar baixa (volta a conta pra aberta)">
                        {estornarBaixa.isPending && estornarBaixa.variables === l.conta_financeira_id ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                      </button>
                    ) : (
                      <span className="text-[9px] text-ink-300" title="Lançamento de transferência/automático — estorne pela origem">—</span>
                    )}
                  </td>
                </tr>
                {expandido.has(l.id) && (
                  <tr className="bg-ink-50">
                    <td colSpan={10} className="p-3">
                      <div className="text-[11px] space-y-1">
                        {l.observacao && (
                          <div><strong className="text-ink-700">Observação:</strong> <span className="whitespace-pre-wrap text-ink-600">{l.observacao}</span></div>
                        )}
                        {l.conta_financeira_id && (
                          <div><strong className="text-ink-700">Vínculo:</strong> Conta financeira #{l.conta_financeira_id}</div>
                        )}
                        {l.conciliado && l.conciliado_em && (
                          <div><strong className="text-ink-700">Conciliado:</strong> {l.conciliado_por_nome || '—'} em {new Date(l.conciliado_em).toLocaleString('pt-BR')}</div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {showNovo && <ModalNovo onClose={() => setShowNovo(false)} contas={contas} cgs={cgs} qc={qc} />}
      {editar && (
        <ModalEditar
          lanc={editar}
          cgs={cgs}
          onClose={() => setEditar(null)}
          onSalvar={(body: any) => salvarEdicao.mutate({ id: editar.id, body })}
          salvando={salvarEdicao.isPending}
        />
      )}
    </div>
  )
}

function ModalEditar({ lanc, cgs, onClose, onSalvar, salvando }: any) {
  const avulso = lanc.origem === 'manual' && !lanc.transferencia_id
  const [form, setForm] = useState({
    descricao: lanc.descricao || '',
    conta_gerencial_id: (lanc.conta_gerencial_id ?? '') as '' | number,
    observacao: lanc.observacao || '',
    data: (lanc.data || '').slice(0, 10),
    tipo: lanc.tipo || 'despesa',
    valor: String(lanc.valor ?? ''),
  })
  const tiposCg = form.tipo === 'receita' ? CG_RECEITA : CG_DESPESA
  const cgsFiltradas = (cgs || []).filter((c: any) => tiposCg.includes(c.tipo))

  const salvar = () => {
    const body: any = {
      descricao: form.descricao,
      conta_gerencial_id: form.conta_gerencial_id ? Number(form.conta_gerencial_id) : null,
      observacao: form.observacao || null,
      data: form.data || null,
    }
    if (avulso) {
      body.tipo = form.tipo
      body.valor = parseFloat(form.valor)
    }
    onSalvar(body)
  }

  return (
    <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0">
          <h3 className="font-semibold">Editar Lançamento #{lanc.id}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-auto flex-1 min-h-0">
          {!avulso && (
            <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded p-2">
              Lançamento de <strong>{lanc.origem}</strong>: dá pra reclassificar (descrição, conta
              gerencial, observação, data), mas o <strong>valor/tipo</strong> vem da operação de
              origem — pra corrigir dinheiro, estorne pela origem.
            </div>
          )}
          <div>
            <label className="label">Tipo</label>
            <div className="grid grid-cols-2 gap-2">
              {['receita', 'despesa'].map(t => (
                <button key={t} disabled={!avulso}
                  onClick={() => setForm({ ...form, tipo: t, conta_gerencial_id: '' })}
                  className={`py-2.5 rounded-lg text-sm font-medium border-2 disabled:opacity-50 ${
                    form.tipo === t
                      ? t === 'receita'
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-red-500 bg-red-50 text-red-700'
                      : 'border-ink-200'
                  }`}>{t}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="label">Conta Gerencial</label>
            <select className="input" value={form.conta_gerencial_id}
              onChange={e => setForm({ ...form, conta_gerencial_id: e.target.value ? Number(e.target.value) : '' })}>
              <option value="">— sem classificação —</option>
              {cgsFiltradas.map((c: any) => (
                <option key={c.id} value={c.id}>{c.codigo} — {c.nome}</option>
              ))}
            </select>
            <p className="text-xs text-ink-500 mt-1">Classifica o lançamento na DRE.</p>
          </div>
          <div><label className="label">Descrição</label>
            <input className="input" value={form.descricao}
              onChange={e => setForm({ ...form, descricao: e.target.value })} /></div>
          <div><label className="label">Valor</label>
            <input type="number" step="0.01" className="input text-lg font-bold" value={form.valor}
              disabled={!avulso}
              onChange={e => setForm({ ...form, valor: e.target.value })} />
            {!avulso && <p className="text-xs text-ink-400 mt-1">Travado — valor da operação de origem.</p>}
          </div>
          <div><label className="label">Data</label>
            <input type="date" className="input" value={form.data}
              onChange={e => setForm({ ...form, data: e.target.value })} /></div>
          <div><label className="label">Observação</label>
            <textarea className="input min-h-[60px]" value={form.observacao}
              onChange={e => setForm({ ...form, observacao: e.target.value })} /></div>
        </div>
        <div className="px-5 py-4 border-t flex gap-2 shrink-0">
          <button onClick={onClose} className="btn-secondary flex-1">Cancelar</button>
          <button onClick={salvar} disabled={salvando || !form.descricao}
            className="btn-primary flex-1">{salvando ? 'Salvando…' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}

function CardTot({ label, valor, cor, fmt = 'money' }: any) {
  const cores: any = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
    red: 'bg-red-50 border-red-200 text-red-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
  }
  return (
    <div className={`border rounded p-2 ${cores[cor]}`}>
      <div className="text-[9px] uppercase font-semibold opacity-70">{label}</div>
      <div className="text-base font-bold font-mono">
        {fmt === 'money' ? formatMoney(valor) : valor.toLocaleString('pt-BR')}
      </div>
    </div>
  )
}

// Tipos de conta gerencial coerentes com receita/despesa do lançamento
const CG_RECEITA = ['receita', 'deducao', 'receita_fin']
const CG_DESPESA = ['custo', 'despesa_op', 'despesa_fin']

function ModalNovo({ onClose, contas, cgs, qc }: any) {
  const [form, setForm] = useState({
    conta_id: '', tipo: 'receita', descricao: '',
    valor: '', data: new Date().toISOString().split('T')[0], observacao: '', carga_id: '',
    conta_gerencial_id: '' as '' | number,
  })
  const tiposCg = form.tipo === 'receita' ? CG_RECEITA : CG_DESPESA
  const cgsFiltradas = (cgs || []).filter((c: any) => tiposCg.includes(c.tipo))
  // Cargas de distribuição em aberto — campo inteligente: vincula a despesa a uma carga.
  const { data: cargas = [] } = useQuery<any[]>({
    queryKey: ['cargas-para-lancamento'],
    queryFn: async () => (await api.get('/vendas/cargas/', { params: { tipo: 'distribuicao' } })).data,
  })
  const criar = useMutation({
    mutationFn: async () => (await api.post('/financeiro/lancamentos', {
      ...form, conta_id: Number(form.conta_id), valor: parseFloat(form.valor),
      carga_id: form.carga_id ? Number(form.carga_id) : null,
      conta_gerencial_id: form.conta_gerencial_id ? Number(form.conta_gerencial_id) : null,
    })).data,
    onSuccess: () => {
      toast.success('Lançado')
      onClose()
      qc.invalidateQueries()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  return (
    <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0">
          <h3 className="font-semibold">Novo Lançamento</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-auto flex-1 min-h-0">
          <div>
            <label className="label">Conta</label>
            <select className="input" value={form.conta_id}
              onChange={e => setForm({ ...form, conta_id: e.target.value })}>
              <option value="">Selecione</option>
              {contas.map((c: any) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Tipo</label>
            <div className="grid grid-cols-2 gap-2">
              {['receita', 'despesa'].map(t => (
                <button key={t} onClick={() => setForm({ ...form, tipo: t, conta_gerencial_id: '' })}
                  className={`py-2.5 rounded-lg text-sm font-medium border-2 ${
                    form.tipo === t
                      ? t === 'receita'
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-red-500 bg-red-50 text-red-700'
                      : 'border-ink-200'
                  }`}>{t}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="label">Conta Gerencial</label>
            <select className="input" value={form.conta_gerencial_id}
              onChange={e => setForm({ ...form, conta_gerencial_id: e.target.value ? Number(e.target.value) : '' })}>
              <option value="">— selecione —</option>
              {cgsFiltradas.map((c: any) => (
                <option key={c.id} value={c.id}>{c.codigo} — {c.nome}</option>
              ))}
            </select>
            <p className="text-xs text-ink-500 mt-1">Classifica o lançamento na DRE.</p>
          </div>
          <div><label className="label">Descrição</label>
            <input className="input" value={form.descricao}
              onChange={e => setForm({ ...form, descricao: e.target.value })} /></div>
          <div><label className="label">Valor</label>
            <input type="number" className="input text-lg font-bold" value={form.valor}
              onChange={e => setForm({ ...form, valor: e.target.value })} /></div>
          <div><label className="label">Data</label>
            <input type="date" className="input" value={form.data}
              onChange={e => setForm({ ...form, data: e.target.value })} /></div>
          <div><label className="label">Observação</label>
            <textarea className="input min-h-[60px]" value={form.observacao}
              onChange={e => setForm({ ...form, observacao: e.target.value })} /></div>
          <div>
            <label className="label">Carga (opcional)</label>
            <select className="input" value={form.carga_id}
              onChange={e => setForm({ ...form, carga_id: e.target.value })}>
              <option value="">Sem carga (lançamento normal)</option>
              {cargas.map((c: any) => (
                <option key={c.id} value={c.id}>Carga #{c.numero}{c.nome ? ` · ${c.nome}` : ''} ({c.status})</option>
              ))}
            </select>
            {form.carga_id && <p className="text-xs text-ink-500 mt-1">Sai no resumo da carga selecionada.</p>}
          </div>
        </div>
        <div className="px-5 py-4 border-t flex gap-2 shrink-0">
          <button onClick={onClose} className="btn-secondary flex-1">Cancelar</button>
          <button onClick={() => criar.mutate()}
            disabled={!form.conta_id || !form.descricao || !form.valor}
            className="btn-primary flex-1">Lançar</button>
        </div>
      </div>
    </div>
  )
}
